import { useEffect, useMemo, useState } from 'react';
import { DataTable, Drawer, Segmented, StatusChip, type Column } from '../components/data';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, PageHead, VendorPicker } from '../components/ui';
import { api } from '../lib/api';
import { ProductImportDialog, type ImportResult } from './ProductImportDialog';
import { formatNumber } from '../lib/format';
import { useResource } from '../lib/hooks';
import type {
  Me,
  Product,
  ProductCategoryRow,
  ProductDetail,
  ProductPeriod,
  ProductSummary,
  Vendor,
} from '../lib/types';

/**
 * The product catalogue — the price book.
 *
 * What we sell, who we buy it from, how it bills and what it costs per unit.
 * Sales prices a proposal from it and Account Management points a
 * recommendation at it, which is why it sits under Revenue rather than inside
 * either module: it belongs to both and is owned by neither.
 *
 * 304 rows means the page is a filter before it is a list. Category leads
 * because that is how somebody looking for "a backup product" thinks, and the
 * search box also reads descriptions, which the table does not show.
 */

const PERIODS: ProductPeriod[] = ['Monthly', 'Yearly', 'One-time'];

const BLANK = {
  name: '',
  description: '',
  vendorId: null as number | null,
  categoryId: '' as number | '',
  periodType: 'Monthly' as ProductPeriod,
  unitPrice: '0',
  needsReview: false,
};

/** Twelve months of it, whatever the billing period. The only way to compare a
 *  $26.40/month licence with a $312/year one on the same page. */
export function annualisedPrice(unitPrice: number, period: ProductPeriod): number {
  if (period === 'Monthly') return unitPrice * 12;
  if (period === 'Yearly') return unitPrice;
  return 0; // One-time is not recurring revenue, so it annualises to nothing.
}

export function ProductCatalogPage() {
  const summary = useResource<ProductSummary>('/products/summary');

  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [vendor, setVendor] = useState('');
  const [period, setPeriod] = useState('');
  const [scope, setScope] = useState<'active' | 'all'>('active');
  const [editing, setEditing] = useState<Product | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* The search runs server-side across three columns, so binding it straight
     to the box would be a scan per keystroke — the same reason the NOC library
     and the admin registers debounce theirs. */
  useEffect(() => {
    const timer = setTimeout(() => setQuery(term), 220);
    return () => clearTimeout(timer);
  }, [term]);

  const path = useMemo(() => {
    const params = new URLSearchParams();
    if (query.trim()) params.set('q', query.trim());
    if (category) params.set('category', category);
    if (vendor) params.set('vendor', vendor);
    if (period) params.set('period', period);
    if (scope === 'all') params.set('includeInactive', '1');
    const qs = params.toString();
    return `/products${qs ? `?${qs}` : ''}`;
  }, [query, category, vendor, period, scope]);

  const products = useResource<Product[]>(path);

  const rows = products.data ?? [];

  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [importing, setImporting] = useState(false);
  const [importNote, setImportNote] = useState<string | null>(null);

  /** What the import did, said once it has: see ProductImportDialog. */
  function imported(r: ImportResult) {
    setImporting(false);
    setImportNote(
      `Imported ${formatNumber(r.products)} Backup and Software products and ${formatNumber(r.services)} services: ` +
        `${formatNumber(r.added)} added, ${formatNumber(r.updated)} overwritten. ` +
        `${formatNumber(r.deleted)} other products deleted` +
        (r.retired ? `, ${formatNumber(r.retired)} retired because something still uses them` : '') +
        '.' +
        (r.categoriesDeleted.length
          ? ` ${r.categoriesDeleted.length} categories deleted, with ${formatNumber(r.matrixTicksRemoved)} client product-matrix ticks.`
          : '') +
        (r.skipped.length ? ` ${r.skipped.length} skipped as a second item of the same name: ${r.skipped.slice(0, 5).join(', ')}${r.skipped.length > 5 ? '…' : ''}.` : ''),
    );
    reload();
  }

  const reload = () => {
    products.reload();
    summary.reload();
  };

  const s = summary.data;

  const columns: Column<Product>[] = [
    {
      key: 'Name',
      label: 'Service',
      render: (p) => (
        <button type="button" className="admin-link-button" onClick={() => setEditing(p)}>
          <span className="strong">{p.Name}</span>
          {p.Description && p.Description !== p.Name && (
            <span className="ui-cell-sub">{p.Description}</span>
          )}
        </button>
      ),
    },
    { key: 'Vendor', label: 'Vendor', render: (p) => p.Vendor ?? '—' },
    { key: 'Category', label: 'Category', render: (p) => p.Category ?? '—' },
    { key: 'PeriodType', label: 'Bills', render: (p) => p.PeriodType },
    {
      key: 'UnitPrice',
      label: 'Unit price',
      align: 'right',
      render: (p) => (
        <span className="mono">{formatNumber(p.UnitPrice, 'currency')}</span>
      ),
    },
    {
      key: 'Annual',
      label: 'Per year',
      align: 'right',
      render: (p) => (
        <span className="mono subtle">
          {p.PeriodType === 'One-time'
            ? '—'
            : formatNumber(annualisedPrice(p.UnitPrice, p.PeriodType), 'currency')}
        </span>
      ),
    },
    {
      key: 'Flags',
      label: '',
      render: (p) => (
        <span className="row">
          {!p.IsActive && <StatusChip band="neutral">Retired</StatusChip>}
          {(p.TimesRecommended ?? 0) > 0 && (
            <span className="xs subtle">
              recommended {p.TimesRecommended}×
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHead
        title="Product Catalogue"
        lede="The price book: what we sell, who we buy it from, how it bills and what it costs per unit. Sales quotes from it and account management recommends from it, so there is one list rather than one per desk."
        actions={
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            {isAdmin && (
              <button
                type="button"
                className="btn btn-secondary"
                disabled={importing}
                onClick={() => {
                  setImportNote(null);
                  setImporting(true);
                }}
                title="Add and update the price book from every Product, Service and Service Bundle in Autotask"
              >
                Import from Autotask
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              New product
            </button>
          </div>
        }
      />
      {importing && <ProductImportDialog onClose={() => setImporting(false)} onDone={imported} />}
      {importNote && <p className="sub mb-4">{importNote}</p>}

      {error && <ErrorBanner message={error} />}
      {summary.error && <ErrorBanner message={summary.error} />}

      {s && (
        <div className="grid grid-3 mb-4">
          <div className="stat">
            <div className="label">In the catalogue</div>
            <div className="figure">{s.Active}</div>
            <div className="foot">
              sellable services from {s.Vendors} {s.Vendors === 1 ? 'vendor' : 'vendors'}
            </div>
          </div>
          <div className="stat">
            <div className="label">Recurring</div>
            <div className="figure">{s.Monthly}</div>
            <div className="foot">bill monthly · the rest annually</div>
          </div>
          {/* The number on this page anybody can act on: a backlog, not a
              health score. */}
          <div className="stat">
            <div className="label">Unpriced</div>
            <div className={`figure${s.Unpriced > 0 ? ' accent' : ''}`}>{s.Unpriced}</div>
            <div className="foot">
              at zero — cannot be quoted from
              {s.Uncategorised > 0 && ` · ${s.Uncategorised} with no category`}
            </div>
          </div>
        </div>
      )}

      <div className="row-between wrap mb-4">
        <div className="row wrap">
          <input
            className="input"
            style={{ width: 260 }}
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search name, description or vendor"
            aria-label="Search the catalogue"
          />
          <select
            className="select"
            style={{ width: 'auto' }}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="Category"
          >
            <option value="">All categories</option>
            {(s?.categories ?? []).map((c) => (
              <option key={c.CategoryId} value={c.Category}>
                {c.Category} ({c.Count})
              </option>
            ))}
          </select>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={vendor}
            onChange={(e) => setVendor(e.target.value)}
            aria-label="Vendor"
          >
            <option value="">All vendors</option>
            {(s?.vendors ?? []).map((v) => (
              <option key={v.Vendor} value={v.Vendor}>
                {v.Vendor} ({v.Count})
              </option>
            ))}
          </select>
          <select
            className="select"
            style={{ width: 'auto' }}
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            aria-label="Billing period"
          >
            <option value="">Any period</option>
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>

        <Segmented
          label="Which products"
          value={scope}
          onChange={setScope}
          options={[
            { id: 'active', label: 'Sellable' },
            { id: 'all', label: 'Including retired' },
          ]}
        />
      </div>

      {products.loading && <Loading label="Loading the catalogue" />}
      {products.error && <ErrorBanner message={products.error} />}

      {!products.loading &&
        (rows.length === 0 ? (
          <Empty title="Nothing matches">
            <p>
              No service matches those filters. The catalogue is seeded from the PSA's service
              export — if it is empty entirely, that seed has not run against this database.
            </p>
          </Empty>
        ) : (
          <>
            <p className="xs subtle mb-4">
              {rows.length} {rows.length === 1 ? 'service' : 'services'}
            </p>
            <DataTable columns={columns} rows={rows} rowKey={(p) => String(p.ProductId)} />
          </>
        ))}

      {editing && (
        <ProductDrawer
          product={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
          onError={(message) => setError(message)}
        />
      )}
    </>
  );
}

function ProductDrawer({
  product,
  onClose,
  onSaved,
  onError,
}: {
  product: Product | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  /* Loaded rather than passed for the detail: where a product has been
     recommended is the thing worth knowing before changing its price, and the
     list endpoint has no business carrying it for 304 rows. */
  const detail = useResource<ProductDetail>(
    product ? `/products/${product.ProductId}` : null,
  );

  /* The categories are the rows of every client's product matrix, so they are
     chosen from the list rather than typed. A typo here would add a matrix row
     nobody can tick off. */
  const categories = useResource<ProductCategoryRow[]>('/products/categories');
  /* Same reason as the categories, and the same fix for the same bug: the
     vendor used to be a free-text box, and the estate ended up holding four
     spellings of ConnectWise. See migration 097. */
  const vendors = useResource<Vendor[]>('/vendors');

  const [form, setForm] = useState({
    ...BLANK,
    ...(product
      ? {
          name: product.Name,
          description: product.Description ?? '',
          vendorId: product.VendorId,
          categoryId: (product.CategoryId ?? '') as number | '',
          periodType: product.PeriodType,
          unitPrice: String(product.UnitPrice),
          needsReview: product.NeedsReview,
        }
      : {}),
  });
  const [saving, setSaving] = useState(false);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      vendorId: form.vendorId,
      categoryId: form.categoryId === '' ? null : Number(form.categoryId),
      periodType: form.periodType,
      unitPrice: Number(form.unitPrice.replace(/[^0-9.-]/g, '')) || 0,
      needsReview: form.needsReview,
    };
    try {
      if (product) await api.put(`/products/${product.ProductId}`, payload);
      else await api.post('/products', payload);
      onSaved();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  const used = detail.data?.recommendations ?? [];

  return (
    <Drawer
      open
      onClose={onClose}
      title={product ? product.Name : 'New product'}
      subtitle={product?.Vendor ?? undefined}
      footer={
        <>
          {product && product.IsActive && (
            <DeleteButton
              what={`"${product.Name}" from the catalogue`}
              label="Retire"
              onConfirm={() =>
                void api
                  .del(`/products/${product.ProductId}`)
                  .then(onSaved)
                  .catch((err: unknown) =>
                    onError(err instanceof Error ? err.message : 'Could not retire it'),
                  )
              }
            />
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.name.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <Field label="Service" hint="Unique. This is the name a quote prints.">
        <input
          className="input"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          autoFocus
        />
      </Field>

      <Field label="Description">
        <textarea
          className="textarea"
          rows={3}
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
        />
      </Field>

      <div className="form-grid">
        <Field
          label="Vendor"
          hint="From the shared directory. A new one is added in Settings first."
        >
          <VendorPicker
            vendors={vendors.data ?? []}
            value={form.vendorId}
            onChange={(vendorId) => set({ vendorId })}
          />
        </Field>
        <Field
          label="Category"
          hint="Also a row on every client's product matrix, which is why it is a list."
        >
          <select
            className="select"
            value={String(form.categoryId)}
            onChange={(e) => set({ categoryId: e.target.value ? Number(e.target.value) : '' })}
          >
            <option value="">No category — appears on no matrix</option>
            {(categories.data ?? []).map((c) => (
              <option key={c.CategoryId} value={c.CategoryId}>
                {c.Name}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Bills">
          <select
            className="select"
            value={form.periodType}
            onChange={(e) => set({ periodType: e.target.value as ProductPeriod })}
          >
            {PERIODS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </Field>
        <Field
          label="Unit price"
          hint="Per seat, device or whatever it is sold by. Negative for a discount line."
        >
          <input
            className="input"
            inputMode="decimal"
            value={form.unitPrice}
            onChange={(e) => set({ unitPrice: e.target.value })}
          />
        </Field>
      </div>


      {product && (
        <div className="mt-6">
          <p className="eyebrow">Where it has been recommended</p>
          {detail.loading && <Loading label="Loading" />}
          {!detail.loading && used.length === 0 ? (
            <p className="small muted mt-2">
              Never quoted on a recommendation. That is not a reason to retire it — but it is the
              first thing to check when deciding.
            </p>
          ) : (
            <div className="stack mt-2" style={{ gap: 'var(--space-2)' }}>
              {used.map((r) => (
                <div key={r.AmRecommendationId} className="mini-row">
                  <div className="grow">
                    <div className="strong small">{r.Title ?? 'Untitled'}</div>
                    <div className="xs muted">
                      {[r.ClientName, r.Status].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <span className="mono small">
                    {r.Quantity} × {formatNumber(r.UnitPrice, 'currency')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}
